import { useState } from 'react';
import { modals } from '@mantine/modals';

import { useShowResponseStatus } from '@/shared';
import {
  useGetConfigImagesQuery,
  useUploadConfigImageMutation,
  useDeleteConfigImageMutation,
} from '../store/config-api';

import type { TGetConfigImagesParams, TConfigDeleteImageParams } from '../store/types';

const initialParams: TGetConfigImagesParams = {
  pageSize: 20,
  page: 1,
  ascending: false,
};

export const useConfigImages = () => {
  const [params, setParams] = useState<TGetConfigImagesParams>(initialParams);

  const {
    data: data,
    isLoading: isGetLoading,
    isError: isGetError,
  } = useGetConfigImagesQuery(params);
  const itemsCount = data?.count;
  const items = data?.items ?? [];

  const [
    uploadImage, {
      isLoading: isUploadLoading,
      isSuccess: isUploadSuccess,
      isError: isUploadError,
    }
  ] = useUploadConfigImageMutation();

  const [
    deleteImage, {
      isLoading: isDeleteLoading,
      isSuccess: isDeleteSuccess,
      isError: isDeleteError,
    }
  ] = useDeleteConfigImageMutation();

  const isLoading = isGetLoading || isUploadLoading || isDeleteLoading;

  useShowResponseStatus({
    isSuccess: isUploadSuccess || isDeleteSuccess,
    isError: isGetError || isUploadError || isDeleteError,
  });

  const handleUploadImage = async (selectedFile: File | null) => {
    const formData = new FormData();

    if (selectedFile) {
      formData.append('file', selectedFile);
      await uploadImage({ body: formData });
    }
  };

  const handleDeleteImage = (values: TConfigDeleteImageParams) => {
    modals.openConfirmModal({
      title: `Удаление картинки c ID ${values.imageId}`,
      centered: true,
      children: 'Вы точно хотите удалить файл?',
      labels: { confirm: 'Подтвердить', cancel: 'Отмена' },
      confirmProps: { color: 'red' },
      onConfirm: () => deleteImage(values),
    });
  };

  const handlePageChange = (page: number) => {
    setParams((prev) => ({
      ...prev,
      page,
    }));
  };

  return {
    params,
    items,
    itemsCount,
    isLoading,
    handleUploadImage,
    handleDeleteImage,
    handlePageChange,
  };
};
